import {
  CREATE_OR_UPDATE_INCORPORATION_CONTACT_API,
  DELETE_INCORPORATION_CONTACT_API,
} from "@/constant";
import type { IncorporationContact } from "@/schemas/incorporation";
import { apiRequest } from "@/utils";

// Response types
export type CreateOrUpdateContactSuccessResponse = {
  success: true;
  id: number;
  data: IncorporationContact;
};

export type CreateOrUpdateContactErrorResponse = {
  success: false;
  error?: string;
  errors?: string[];
};

export type CreateOrUpdateContactResponse =
  | CreateOrUpdateContactSuccessResponse
  | CreateOrUpdateContactErrorResponse;

// Delete response types
export type DeleteContactSuccessResponse = {
  success: true;
  message: string;
};

export type DeleteContactErrorResponse = {
  success: false;
  error?: string;
  errors?: string[];
};

export type DeleteContactResponse =
  | DeleteContactSuccessResponse
  | DeleteContactErrorResponse;

// Custom error class for API errors
export class IncorporationContactError extends Error {
  statusCode: number;
  errors?: string[];

  constructor(
    message: string,
    statusCode: number,
    errors?: string[]
  ) {
    super(message);
    this.name = "IncorporationContactError";
    this.statusCode = statusCode;
    this.errors = errors;
  }
}

/**
 * Creates or updates an incorporation contact
 * @param data - Partial IncorporationContact data. If `id` is present, updates existing contact; if absent, creates new one.
 * @returns Promise resolving to success response with id and full data
 * @throws IncorporationContactError for API errors (404, 403, 422, etc.)
 */
export async function createOrUpdateIncorporationContact(
  data: Partial<IncorporationContact>
): Promise<CreateOrUpdateContactSuccessResponse> {
  try {
    const response = await apiRequest(
      CREATE_OR_UPDATE_INCORPORATION_CONTACT_API,
      "POST",
      data,
      false,
      { throwOnError: false }
    );

    const responseData = response.data as CreateOrUpdateContactResponse;
    const status = response.status;
    const ok = response.ok;

    // Handle success response (200)
    if (ok && responseData.success === true) {
      return responseData;
    }

    // Handle error responses
    if (!responseData.success) {
      // 404 - Contact or Request not found
      if (status === 404) {
        throw new IncorporationContactError(
          responseData.error || "Registro no encontrado",
          404
        );
      }

      // 403 - Unauthorized
      if (status === 403) {
        throw new IncorporationContactError(
          responseData.error || "No autorizado para acceder a este registro",
          403
        );
      }

      // 422 - Validation errors
      if (status === 422) {
        throw new IncorporationContactError(
          responseData.error || "Error de validación",
          422,
          responseData.errors
        );
      }

      // Other error status codes
      throw new IncorporationContactError(
        responseData.error || `Error en la solicitud (${status})`,
        status,
        responseData.errors
      );
    }

    // Fallback for unexpected response format
    throw new IncorporationContactError(
      "Respuesta inesperada del servidor",
      status
    );
  } catch (error) {
    // Re-throw if it's already an IncorporationContactError
    if (error instanceof IncorporationContactError) {
      throw error;
    }

    // Handle network errors or other fetch errors
    if (error instanceof Error) {
      throw new IncorporationContactError(
        `Error de red: ${error.message}`,
        0
      );
    }

    // Fallback for unknown errors
    throw new IncorporationContactError("Error desconocido", 0);
  }
}

/**
 * Deletes an incorporation contact by id
 * @param id - ID of the incorporation contact to delete
 * @returns Promise resolving to success response with confirmation message
 * @throws IncorporationContactError for API errors (404, 403, 422, etc.)
 */
export async function deleteIncorporationContact(
  id: number
): Promise<DeleteContactSuccessResponse> {
  try {
    const response = await apiRequest(
      `${DELETE_INCORPORATION_CONTACT_API}?id=${id}`,
      "DELETE",
      null,
      false,
      { throwOnError: false }
    );

    const responseData = response.data as DeleteContactResponse;
    const status = response.status;
    const ok = response.ok;

    // Handle success response (200)
    if (ok && responseData.success === true) {
      return responseData;
    }

    // Handle error responses
    if (!responseData.success) {
      // 404 - Contact not found
      if (status === 404) {
        throw new IncorporationContactError(
          responseData.error || "Contacto no encontrado",
          404
        );
      }

      // 403 - Unauthorized
      if (status === 403) {
        throw new IncorporationContactError(
          responseData.error ||
            "No autorizado para eliminar este contacto",
          403
        );
      }

      // 422 - Validation errors
      if (status === 422) {
        throw new IncorporationContactError(
          responseData.error || "Error de validación",
          422,
          responseData.errors
        );
      }

      // Other error status codes
      throw new IncorporationContactError(
        responseData.error || `Error en la solicitud (${status})`,
        status,
        responseData.errors
      );
    }

    // Fallback for unexpected response format
    throw new IncorporationContactError(
      "Respuesta inesperada del servidor",
      status
    );
  } catch (error) {
    // Re-throw if it's already an IncorporationContactError
    if (error instanceof IncorporationContactError) {
      throw error;
    }

    // Handle network errors or other fetch errors
    if (error instanceof Error) {
      throw new IncorporationContactError(
        `Error de red: ${error.message}`,
        0
      );
    }

    // Fallback for unknown errors
    throw new IncorporationContactError("Error desconocido", 0);
  }
}
